import { useEffect, useMemo, useState } from "react";
import { useRoute } from "wouter";
import { useNavegacao } from "@/lib/navegacao";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Lock, RotateCcw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  useAuditoria, usePatchAuditoria, usePatchItemAuditoria, useFecharAuditoria, useReabrirAuditoria,
  parseErroAuditoria, nota1, corDaNota,
  type AuditoriaDetalhe, type AuditoriaItemDetalhe, type TipoAuditoria, type AuditoriaExistente,
} from "@/hooks/use-auditoria";

import { PageHeader } from "@/components/layout/page-header";
// ─── Helpers locais ───────────────────────────────────────────────────────────

function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = iso.slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : iso;
}

/** Trackpad rolando a página com o campo focado decrementa o número — sempre desfoca no scroll. */
function semScroll(e: React.WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur();
}

/** Média simples: soma ÷ quantidade. Sem peso em lugar nenhum. Espelha mediaSimplesCriterios do servidor. */
function media(notas: number[]): number | null {
  if (!notas.length) return null;
  return Math.round((notas.reduce((s, n) => s + n, 0) / notas.length + Number.EPSILON) * 100) / 100;
}

function notasNumericas(notas: Array<{ nota: string }>): number[] {
  return notas.map(n => parseFloat(n.nota)).filter(n => !isNaN(n));
}

function notasLancadas(auditoria: AuditoriaDetalhe): number[] {
  return auditoria.itens.flatMap(it => notasNumericas(it.notas));
}

function mediaAtividade(item: AuditoriaItemDetalhe): number | null {
  return media(notasNumericas(item.notas));
}

// ─── Campo de nota de um critério ──────────────────────────────────────────────

function CriterioLinha({
  nome, valorInicial, onSalvar, disabled,
}: {
  nome: string;
  valorInicial: string;
  onSalvar: (nota: number | null) => void;
  disabled: boolean;
}) {
  const { toast } = useToast();
  const [valor, setValor] = useState(valorInicial);
  useEffect(() => { setValor(valorInicial); }, [valorInicial]);

  const commit = () => {
    const bruto = valor.trim().replace(",", ".");
    if (bruto === "") {
      if (valorInicial !== "") onSalvar(null);
      return;
    }
    const n = parseFloat(bruto);
    if (isNaN(n) || n < 0 || n > 10) {
      toast({ title: "Nota inválida", description: "Informe um valor de 0 a 10.", variant: "destructive" });
      setValor(valorInicial);
      return;
    }
    if (valorInicial !== "" && parseFloat(valorInicial) === n) return;
    onSalvar(n);
  };

  const vazio = valor.trim() === "";

  return (
    <div className="flex items-center gap-3 py-2.5 border-b last:border-b-0">
      <span className="flex-1 text-sm font-medium leading-tight">{nome}</span>
      {disabled ? (
        <span className={`w-[84px] text-center text-2xl font-bold ${vazio ? "text-muted-foreground/50" : corDaNota(valor)}`}>
          {vazio ? "—" : nota1(valor)}
        </span>
      ) : (
        <Input
          type="number" step="0.1" min="0" max="10" inputMode="decimal"
          placeholder="—"
          value={valor}
          onChange={e => setValor(e.target.value)}
          onBlur={commit}
          onWheel={semScroll}
          className={`w-[84px] h-14 px-0 text-center text-2xl font-bold border-2 ${vazio ? "" : corDaNota(valor)}`}
        />
      )}
    </div>
  );
}

// ─── Card de uma atividade ────────────────────────────────────────────────────

function ItemCard({ auditoriaId, item, somenteLeitura }: {
  auditoriaId: string; item: AuditoriaItemDetalhe; somenteLeitura: boolean;
}) {
  const { toast } = useToast();
  const patch = usePatchItemAuditoria();
  const [obsAberta, setObsAberta] = useState(false);
  const [observacao, setObservacao] = useState(item.observacao ?? "");
  useEffect(() => { setObservacao(item.observacao ?? ""); }, [item.observacao]);

  const notaPorCriterio = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of item.notas) m.set(n.audCriterioId, n.nota);
    return m;
  }, [item.notas]);

  const med = mediaAtividade(item);

  const salvarCriterio = (audCriterioId: string, nota: number | null) => {
    patch.mutate(
      { auditoriaId, atividadeId: item.audAtividadeId, data: { criterios: [{ audCriterioId, nota }] } },
      { onError: (e: any) => toast({ title: "Erro ao salvar nota", description: e?.message, variant: "destructive" }) },
    );
  };

  const salvarObservacao = () => {
    const v = observacao.trim();
    if (v === (item.observacao ?? "").trim()) return;
    patch.mutate(
      { auditoriaId, atividadeId: item.audAtividadeId, data: { observacao: v === "" ? null : v } },
      { onError: (e: any) => toast({ title: "Erro ao salvar observação", description: e?.message, variant: "destructive" }) },
    );
  };

  return (
    <Card>
      <CardHeader className="p-4 pb-1">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base leading-tight">{item.atividadeNome}</CardTitle>
          {med !== null && (
            <span className={`text-xs font-semibold ${corDaNota(med)}`}>média {nota1(med)}</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-4 pt-1">
        {item.criteriosCatalogo.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">Esta atividade não tem critérios cadastrados.</p>
        ) : (
          <div>
            {item.criteriosCatalogo.map(c => (
              <CriterioLinha
                key={c.id}
                nome={c.nome}
                valorInicial={notaPorCriterio.get(c.id) ?? ""}
                disabled={somenteLeitura}
                onSalvar={n => salvarCriterio(c.id, n)}
              />
            ))}
          </div>
        )}

        <Button
          variant="ghost" size="sm" className="h-8 px-0 mt-2 text-xs text-muted-foreground"
          onClick={() => setObsAberta(v => !v)}
        >
          {obsAberta ? "Ocultar observação" : item.observacao ? "Observação anotada" : "+ Observação"}
        </Button>
        {obsAberta && (
          <Textarea
            rows={2}
            className="mt-2"
            placeholder="O que chamou atenção"
            value={observacao}
            disabled={somenteLeitura}
            onChange={e => setObservacao(e.target.value)}
            onBlur={salvarObservacao}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── Tipo: orientativa × fechamento — troca mesmo com a auditoria fechada ──────
//
// Trocar o tipo nunca mexe em nota: fechada continua com nota_final congelada.
// Só decide se ESTA auditoria vai para o Financeiro. Virar fechamento é ato
// deliberado (confirmação); voltar pra orientativa não precisa.

function TipoAuditoriaControl({ auditoria }: { auditoria: AuditoriaDetalhe }) {
  const { toast } = useToast();
  const patch = usePatchAuditoria();
  const [conflito, setConflito] = useState<AuditoriaExistente | null>(null);
  const [processando, setProcessando] = useState(false);

  const mudarTipo = async (novoTipo: TipoAuditoria) => {
    if (novoTipo === auditoria.tipo || processando) return;
    if (novoTipo === "fechamento") {
      const confirmado = window.confirm(
        "Marcar como fechamento? Essa passa a ser a nota da competência deste setor — é a que vai para o " +
        "Financeiro. Só pode haver uma auditoria de fechamento por setor por mês.",
      );
      if (!confirmado) return;
    }
    setProcessando(true);
    try {
      await patch.mutateAsync({ id: auditoria.id, data: { tipo: novoTipo } });
      toast({ title: novoTipo === "fechamento" ? "Marcada como fechamento" : "Marcada como orientativa" });
    } catch (e) {
      const erro = parseErroAuditoria(e, "Erro ao trocar o tipo");
      if (erro.code === "FECHAMENTO_JA_EXISTE" && erro.auditoriaExistente) {
        setConflito(erro.auditoriaExistente);
        return;
      }
      toast({ title: "Erro ao trocar o tipo", description: erro.message, variant: "destructive" });
    } finally {
      setProcessando(false);
    }
  };

  const rebaixarEContinuar = async () => {
    if (!conflito) return;
    setProcessando(true);
    try {
      await patch.mutateAsync({ id: conflito.id, data: { tipo: "orientativa" } });
      await patch.mutateAsync({ id: auditoria.id, data: { tipo: "fechamento" } });
      setConflito(null);
      toast({ title: "Marcada como fechamento" });
    } catch (e) {
      toast({ title: "Erro ao trocar o tipo", description: parseErroAuditoria(e).message, variant: "destructive" });
    } finally {
      setProcessando(false);
    }
  };

  return (
    <div className="pt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-muted-foreground shrink-0">Tipo</span>
        <div className="inline-flex rounded-md border overflow-hidden">
          <button
            type="button"
            onClick={() => mudarTipo("orientativa")}
            disabled={processando}
            className={`px-3 py-1 text-xs font-medium transition-colors ${
              auditoria.tipo === "orientativa" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"
            }`}
          >
            Orientativa
          </button>
          <button
            type="button"
            onClick={() => mudarTipo("fechamento")}
            disabled={processando}
            className={`px-3 py-1 text-xs font-medium border-l transition-colors ${
              auditoria.tipo === "fechamento" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground"
            }`}
          >
            Fechamento
          </button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1">
        {auditoria.tipo === "fechamento"
          ? "É a nota da competência deste setor — vai para o Financeiro."
          : "Direciona o time; não vai para o Financeiro."}
      </p>

      <AlertDialog open={!!conflito} onOpenChange={v => !v && setConflito(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Já existe um fechamento para este setor neste mês</AlertDialogTitle>
            <AlertDialogDescription>
              {conflito && (
                <>
                  A auditoria de <strong>{dataBR(conflito.data)}</strong>
                  {conflito.status === "fechada" ? " (fechada" : " (rascunho"}, nota {nota1(conflito.notaFinal)})
                  já é o fechamento deste setor neste mês. Só pode existir uma. Rebaixar a outra para orientativa e
                  marcar esta como fechamento?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={e => { e.preventDefault(); rebaixarEContinuar(); }}
              disabled={processando}
            >
              Rebaixar a outra e continuar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Resumo fixo — nota do setor (calculada, somente leitura) + contas ────────
//
// Não existe tela de fechamento separada, nem revisão que repete as notas no
// rodapé: este resumo fica sempre à vista no topo, tanto em rascunho (a média
// ao vivo) quanto fechada (a nota_final congelada).

function ResumoAuditoria({ auditoria }: { auditoria: AuditoriaDetalhe }) {
  const fechada = auditoria.status === "fechada";
  const lancadas = useMemo(() => notasLancadas(auditoria), [auditoria]);
  const totalCriterios = auditoria.itens.reduce((s, it) => s + it.criteriosCatalogo.length, 0);
  const faltam = totalCriterios - lancadas.length;

  // Fechada: mostra o que foi congelado no banco. Rascunho: acompanha ao vivo
  // conforme o auditor lança — os dois são o mesmo cálculo (mediaSimplesCriterios).
  const notaAtual = fechada ? auditoria.notaFinal : media(lancadas);

  return (
    <Card className={fechada ? "border-sucesso/40" : "border-primary/40"}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Nota do setor</p>
            <p className="text-xs text-muted-foreground">média simples · calculada, não digitada</p>
          </div>
          <p className={`text-2xl font-bold leading-none shrink-0 ${corDaNota(notaAtual)}`}>{nota1(notaAtual)}</p>
        </div>
        <p className="text-xs text-muted-foreground">
          {lancadas.length} nota{lancadas.length === 1 ? "" : "s"} somando{" "}
          {lancadas.reduce((s, n) => s + n, 0).toFixed(1).replace(".", ",")} ÷ {lancadas.length || 0} ={" "}
          <span className="font-semibold">{nota1(notaAtual)}</span>
        </p>
        {faltam > 0 && (
          <p className="text-xs text-atencao">
            {faltam} critério{faltam === 1 ? "" : "s"} sem nota — fica{faltam === 1 ? "" : "m"} de fora da média, não
            conta{faltam === 1 ? "" : "m"} como zero.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────

export default function AuditoriaDetalhePage() {
  const { toast } = useToast();
  // Voltar devolve à lista de auditorias com a busca que a trouxe.
  const { voltar } = useNavegacao();
  const [, params] = useRoute("/manejo/auditoria/:id");
  const id = params?.id;
  const { data: auditoria, isLoading, isError } = useAuditoria(id);
  const patch = usePatchAuditoria();
  const fechar = useFecharAuditoria();
  const reabrir = useReabrirAuditoria();

  const [observacao, setObservacao] = useState<string>("");
  useEffect(() => { setObservacao(auditoria?.observacao ?? ""); }, [auditoria?.observacao]);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !auditoria) {
    return (
      <div className="p-4 sm:p-6 space-y-3">
        <Button variant="ghost" size="sm" className="-ml-2" onClick={() => voltar("/manejo/auditoria")}>
          <ArrowLeft className="h-4 w-4 mr-1" />Auditorias
        </Button>
        <p className="text-sm text-muted-foreground text-center py-8">Auditoria não encontrada.</p>
      </div>
    );
  }

  const fechada = auditoria.status === "fechada";
  const lancadas = notasLancadas(auditoria);
  const totalCriterios = auditoria.itens.reduce((s, it) => s + it.criteriosCatalogo.length, 0);
  const nomesResponsaveis = auditoria.responsaveis.map(r => r.nome).join(" · ") || "sem responsável";

  const salvarObservacaoGeral = async () => {
    const v = observacao.trim();
    if (v === (auditoria.observacao ?? "").trim()) return;
    try {
      await patch.mutateAsync({ id: auditoria.id, data: { observacao: v === "" ? null : v } });
    } catch (e: any) {
      toast({ title: "Erro ao salvar observação", description: e?.message, variant: "destructive" });
    }
  };

  const handleReabrir = async () => {
    const confirmado = window.confirm(
      "A nota do setor será apagada e recalculada quando você fechar de novo. Reabrir esta auditoria?",
    );
    if (!confirmado) return;
    try {
      await reabrir.mutateAsync(auditoria.id);
      toast({ title: "Auditoria reaberta — pode editar as notas de novo." });
    } catch (e: any) {
      toast({ title: "Não foi possível reabrir", description: e?.message, variant: "destructive" });
    }
  };

  const handleSalvar = async () => {
    if (lancadas.length === 0) return;
    const faltam = totalCriterios - lancadas.length;
    if (faltam > 0) {
      const confirmado = window.confirm(
        `${faltam} critério${faltam === 1 ? "" : "s"} ainda sem nota. ` +
        "Eles ficam de fora da média — fechar mesmo assim?",
      );
      if (!confirmado) return;
    }
    try {
      const v = observacao.trim();
      if (v !== (auditoria.observacao ?? "").trim()) {
        await patch.mutateAsync({ id: auditoria.id, data: { observacao: v === "" ? null : v } });
      }
      await fechar.mutateAsync(auditoria.id);
      toast({ title: "Auditoria fechada!" });
      // Fechar = terminou aqui: volta para a lista de onde se veio (com a busca),
      // em vez de empilhar mais uma entrada por cima da auditoria já fechada.
      voltar("/manejo/auditoria");
    } catch (e: any) {
      toast({ title: "Não foi possível fechar", description: e?.message, variant: "destructive" });
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-3 pb-24">
      <Button variant="ghost" size="sm" className="-ml-2" onClick={() => voltar("/manejo/auditoria")}>
        <ArrowLeft className="h-4 w-4 mr-1" />Auditorias
      </Button>

      <PageHeader
        titulo={auditoria.setorNome ?? "Setor removido"}
        descricao={`${dataBR(auditoria.data)} · ${auditoria.auditorNome}`}
        acao={
          fechada ? (
            <Badge className="bg-sucesso-suave text-sucesso hover:bg-sucesso-suave shrink-0">fechada</Badge>
          ) : (
            <Badge variant="outline" className="shrink-0">rascunho</Badge>
          )
        }
      />

      <Card>
        <CardContent className="p-4 space-y-2">
          <p className="text-xs text-muted-foreground">{nomesResponsaveis}</p>
          <TipoAuditoriaControl auditoria={auditoria} />
        </CardContent>
      </Card>

      <ResumoAuditoria auditoria={auditoria} />

      {fechada && (
        <div className="flex items-start gap-2 rounded-md border border-sucesso/30 bg-sucesso-suave p-3">
          <Lock className="h-4 w-4 mt-0.5 shrink-0 text-sucesso" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-sucesso leading-relaxed">
              Esta auditoria está fechada. Os lançamentos ficaram congelados e não podem mais ser alterados.
            </p>
            <Button
              variant="outline" size="sm" className="mt-2 h-7 text-xs bg-white"
              onClick={handleReabrir} disabled={reabrir.isPending}
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" />
              Reabrir para corrigir
            </Button>
          </div>
        </div>
      )}

      {auditoria.itens.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-8">
          Esta auditoria não tem atividades lançadas.
        </p>
      ) : (
        auditoria.itens.map(item => (
          <ItemCard key={item.id} auditoriaId={auditoria.id} item={item} somenteLeitura={fechada} />
        ))
      )}

      <div className="space-y-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Observação geral</Label>
        <Textarea
          rows={3}
          placeholder="Impressão geral do setor, combinados com a equipe"
          value={observacao}
          disabled={fechada}
          onChange={e => setObservacao(e.target.value)}
          onBlur={salvarObservacaoGeral}
        />
      </div>

      {!fechada && (
        <Button
          className="w-full h-12 text-base"
          disabled={lancadas.length === 0 || fechar.isPending || patch.isPending}
          onClick={handleSalvar}
        >
          <Lock className="h-4 w-4 mr-1.5" />
          {fechar.isPending ? "Salvando..." : "Salvar auditoria"}
        </Button>
      )}
      {lancadas.length === 0 && !fechada && (
        <p className="text-xs text-center text-muted-foreground">Lance ao menos uma nota para poder salvar.</p>
      )}
    </div>
  );
}
